import React, { useEffect } from 'react';
import { X, Star, Scan } from 'lucide-react';
import qrCode from '../assets/google-maps-qr.png';

const ReviewModal = ({ isOpen, onClose }) => {
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Also handle mobile viewport height if needed
            const updateVh = () => {
                let vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            };
            updateVh();
            window.addEventListener('resize', updateVh);
            return () => window.removeEventListener('resize', updateVh);
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[10000] overflow-y-auto flex items-center justify-center p-4 bg-black/80 backdrop-blur-md transition-opacity duration-300 animate-fadeIn"
            onClick={handleOverlayClick}
            style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
        >
            {/* Modal Container */}
            <div className="bg-cream rounded-[2rem] shadow-2xl max-w-[360px] w-full relative animate-modalEnter border-2 border-zen-gold/20 flex flex-col overflow-hidden">

                {/* Close Button - More prominent and safely positioned */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-50 text-coffee-900 hover:text-zen-gold transition-all bg-white/90 hover:bg-white rounded-full p-2.5 shadow-xl hover:scale-110 active:scale-95 border border-zen-gold/10"
                    aria-label="Close"
                >
                    <X size={22} />
                </button>

                {/* Main Content Area - Scrollable if needed but centered by default */}
                <div className="p-8 pb-10 flex flex-col max-h-[90vh]">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-4">
                            <div className="flex space-x-1.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className="text-zen-gold fill-zen-gold drop-shadow-sm"
                                        size={28}
                                    />
                                ))}
                            </div>
                        </div>
                        <h2 className="text-3xl font-display font-bold text-coffee-900 mb-2 leading-tight">
                            Share Your Love
                        </h2>
                        <p className="text-coffee-600 text-sm font-medium">
                            Your feedback helps us grow
                        </p>
                    </div>

                    {/* QR Code Container */}
                    <a
                        href="https://maps.app.goo.gl/uw9PiEF1fY36vobJ9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-3xl p-5 shadow-inner border border-stone-200 mb-8 flex flex-col items-center group cursor-pointer hover:border-zen-gold/30 transition-colors"
                    >
                        <img
                            src={qrCode}
                            alt="Scan to review on Google Maps"
                            className="w-full max-w-[200px] mx-auto rounded-lg group-hover:scale-[1.02] transition-transform"
                        />
                        <p className="text-[10px] text-coffee-400 mt-2">Click or scan to open</p>
                    </a>

                    {/* Step-by-Step Instructions */}
                    <div className="space-y-4 mb-8">
                        <div className="flex items-center space-x-4">
                            <div className="bg-zen-gold/10 text-zen-gold p-3 rounded-2xl">
                                <Scan size={20} />
                            </div>
                            <div>
                                <h4 className="font-bold text-coffee-900 text-sm">Step 1: Scan QR</h4>
                                <p className="text-coffee-600 text-xs">Use your camera or a scanner app</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="bg-zen-gold/10 text-zen-gold p-3 rounded-2xl">
                                <Star size={20} />
                            </div>
                            <div>
                                <h4 className="font-bold text-coffee-900 text-sm">Step 2: Rate Us</h4>
                                <p className="text-coffee-600 text-xs">Leave a 5-star review on Google</p>
                            </div>
                        </div>
                    </div>

                    {/* Primary Action Button */}
                    <a
                        href="https://maps.app.goo.gl/uw9PiEF1fY36vobJ9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-zen-gold hover:bg-coffee-900 text-white py-4 px-6 rounded-2xl flex items-center justify-center space-x-2 transition-all duration-300 shadow-xl shadow-zen-gold/20 font-bold text-lg active:scale-[0.98] group"
                    >
                        <span>Click to Review Now</span>
                    </a>
                </div>
            </div>

            <style>{`
                @keyframes modalEnter {
                    from { opacity: 0; transform: scale(0.95) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                .animate-modalEnter {
                    animation: modalEnter 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default ReviewModal;
